import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostListener, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { iconoirPrivacyPolicy, iconoirWarningTriangle, iconoirXmark } from '@ng-icons/iconoir';
import { AuthService } from '../../../core/auth/auth.service';
import { LegalConsentService } from '../../../core/legal/legal-consent.service';
import { Role } from '../../../models/enums';
import { LegalDocumentType, LegalDocumentVersion } from '../../../models/interfaces';
import { DOCUMENT_LABELS, DOCUMENT_LINKS } from '../../shared/legal-document-page/legal-document-page.component';
import { LegalMarkdownService } from '../../shared/legal-document-page/legal-markdown.service';
import { BtnComponent } from '../buttons/btn/btn.component';

/**
 * Une version en attente de consentement, associée à son type de document (pour le titre / le
 * lien de pied de page / le libellé de checkbox) et à son rendu markdown déjà sécurisé — calculé
 * une seule fois par changement de la liste plutôt qu'à chaque cycle de détection de changement.
 */
interface PendingDocument {
  type: LegalDocumentType;
  version: LegalDocumentVersion;
  renderedHtml: string;
}

/**
 * Un document de portée organisation (CGV/DPA) en attente de signature par
 * l'admin — pas de `renderedHtml` ici : contrairement au gate personnel, cette
 * sollicitation ne montre pas le contenu markdown inline, seulement un lien
 * vers `/documents/<slug>` où la signature peut être donnée (P4).
 */
interface OrganisationPendingDocument {
  type: LegalDocumentType;
  version: LegalDocumentVersion;
}

/**
 * Gate de consentement RGPD (P4), montée globalement dans `app.component.html` à côté des autres
 * overlays applicatifs (toasts, undo-snackbar) — jamais dans un module lazy, sinon elle ne
 * surveillerait pas les routes qui ne le chargent pas.
 *
 * Deux états, pilotés par les signals de {@link LegalConsentService} (jamais dupliqués ici) :
 * - **Bannière** non bloquante pendant le délai de grâce (`isBlocking() === false`) : peut être
 *   reportée pour la session courante via un signal en mémoire (pas de `localStorage` — un
 *   rechargement la refait réapparaître, ce qui est le comportement voulu par le brief).
 * - **Modale bloquante** une fois le délai dépassé (`isBlocking() === true`) : ni croix, ni clic
 *   sur le fond, ni Échap ne la ferme ; une checkbox par document, toutes cochées pour débloquer
 *   le bouton d'acceptation.
 *
 * La bannière ouvre la MÊME modale, mais en mode volontaire (fermable) — `isBlocking()` reste la
 * seule source de vérité pour verrouiller la fermeture, jamais un état local dupliqué : `closeModal()`
 * et `onEscape()` sont de simples garde-fous qui vérifient `isBlocking()` avant d'agir.
 */
@Component({
  selector: 'app-legal-consent-gate',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIcon, DatePipe, BtnComponent, RouterLink],
  templateUrl: './legal-consent-gate.component.html',
  styleUrl: './legal-consent-gate.component.scss',
  viewProviders: [provideIcons({ iconoirPrivacyPolicy, iconoirWarningTriangle, iconoirXmark })]
})
export class LegalConsentGateComponent {
  private authService = inject(AuthService);
  private legalConsentService = inject(LegalConsentService);
  private legalMarkdownService = inject(LegalMarkdownService);

  protected readonly documentLabels = DOCUMENT_LABELS;
  protected readonly documentLinks = DOCUMENT_LINKS;

  /** Bandeau masqué pour la session courante — mémoire uniquement, un reload le refait apparaître. */
  #bannerDismissed = signal(false);

  /** Modale ouverte volontairement depuis la bannière (mode fermable, hors période bloquante). */
  #modalOpenedManually = signal(false);

  #checkedVersionIds = signal<ReadonlySet<string>>(new Set());
  #submitting = signal(false);
  #error = signal<string | null>(null);

  isBlocking = computed(() => this.legalConsentService.isBlocking());
  graceDeadline = computed(() => this.legalConsentService.graceDeadline());
  submitting = computed(() => this.#submitting());
  error = computed(() => this.#error());

  /**
   * Documents en attente, avec leur type résolu : `pendingConsent()` ne porte que la version
   * (`LegalDocumentVersion`), le type vit sur `legal_documents` — on le retrouve en recroisant
   * avec `currentVersions()`, indexé par type.
   */
  pendingDocuments = computed<PendingDocument[]>(() => {
    const currentVersions = this.legalConsentService.currentVersions();
    const pendingIds = new Set(this.legalConsentService.pendingConsent().map(version => version.id));
    return (Object.entries(currentVersions) as [LegalDocumentType, LegalDocumentVersion][])
      .filter(([, version]) => pendingIds.has(version.id))
      .map(([type, version]) => ({
        type,
        version,
        renderedHtml: this.legalMarkdownService.renderToSafeHtml(version.content)
      }));
  });

  /**
   * Gate potentiellement active : profil connecté ET au moins un document en attente.
   *
   * Lit `profileRevision()` — `authService.profile` est un getter classique invisible du système
   * de réactivité (même piège que documenté dans `TrialBannerService.latestSubscription`) : sans
   * cette dépendance, ce computed resterait figé sur le premier statut de connexion observé et la
   * gate ne réagirait ni à une connexion ni à une déconnexion sans rechargement de page.
   */
  private isActive = computed(() => {
    this.authService.profileRevision();
    return !!this.authService.profile && this.pendingDocuments().length > 0;
  });

  showBanner = computed(() => this.isActive() && !this.isBlocking() && !this.#bannerDismissed());
  showModal = computed(() => this.isActive() && (this.isBlocking() || this.#modalOpenedManually()));

  /** Libellé du(des) document(s) concerné(s) pour la bannière — un seul nommé, plusieurs génériques. */
  bannerDocumentsLabel = computed(() => {
    const pending = this.pendingDocuments();
    if (pending.length === 1) return this.documentLabels[pending[0].type];
    return 'documents légaux';
  });

  // ── Sollicitation organisation (P4) ─────────────────────────────────────
  //
  // Symétrique du mécanisme personnel ci-dessus, mais volontairement plus
  // léger : PAS de blocage (le brief l'exclut explicitement pour la portée
  // organisation), pas de modale, pas de checkbox — juste une bannière
  // récurrente qui renvoie l'admin vers `/documents/<slug>` où la signature
  // peut être donnée (cf. `app-legal-document-page`, point 4 du brief).

  /** Lit `profileRevision()` pour la même raison que `isActive` ci-dessus (getter non réactif). */
  isOrganisationAdmin = computed(() => {
    this.authService.profileRevision();
    return this.authService.hasRole([Role.SuperAdmin]);
  });

  /**
   * Documents de portée organisation en attente de signature — vide pour un
   * non-admin (pas de sollicitation à afficher) ou si aucune version en
   * vigueur n'est de portée organisation (`organisationPendingConsent` du
   * service reste alors naturellement vide).
   */
  organisationPendingDocuments = computed<OrganisationPendingDocument[]>(() => {
    if (!this.isOrganisationAdmin()) return [];
    const pendingIds = new Set(this.legalConsentService.organisationPendingConsent().map(v => v.id));
    const currentVersions = this.legalConsentService.currentVersions();
    return (Object.entries(currentVersions) as [LegalDocumentType, LegalDocumentVersion][])
      .filter(([, v]) => pendingIds.has(v.id))
      .map(([type, version]) => ({ type, version }));
  });

  /** Nom de l'organisation active, pour personnaliser la bannière — `null` si indisponible. */
  organisationLabel = computed(() => {
    this.authService.profileRevision();
    return this.authService.profile?.organisation?.label ?? null;
  });

  /** Bandeau masqué pour la session courante — mémoire uniquement, même philosophie que `#bannerDismissed`. */
  #organisationBannerDismissed = signal(false);

  showOrganisationBanner = computed(() =>
    this.organisationPendingDocuments().length > 0 && !this.#organisationBannerDismissed()
  );

  dismissOrganisationBanner(): void {
    this.#organisationBannerDismissed.set(true);
  }

  allChecked = computed(() => {
    const pending = this.pendingDocuments();
    if (pending.length === 0) return false;
    const checked = this.#checkedVersionIds();
    return pending.every(doc => checked.has(doc.version.id));
  });

  isChecked(versionId: string): boolean {
    return this.#checkedVersionIds().has(versionId);
  }

  toggleChecked(versionId: string): void {
    this.#checkedVersionIds.update(set => {
      const next = new Set(set);
      if (next.has(versionId)) {
        next.delete(versionId);
      } else {
        next.add(versionId);
      }
      return next;
    });
  }

  openModal(): void {
    this.#modalOpenedManually.set(true);
  }

  /** Fermeture volontaire — no-op si la gate est bloquante (garde-fou, aucun appelant ne devrait y arriver). */
  closeModal(): void {
    if (this.isBlocking()) return;
    this.#modalOpenedManually.set(false);
    this.#error.set(null);
  }

  dismissBanner(): void {
    this.#bannerDismissed.set(true);
  }

  /** Clic sur le fond : ne ferme que si le clic a démarré ET fini sur le fond lui-même (pas un enfant qui bulle). */
  onBackdropClick(event: MouseEvent): void {
    if (event.target !== event.currentTarget) return;
    this.closeModal();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.showModal()) return;
    this.closeModal();
  }

  async acceptAll(): Promise<void> {
    if (!this.allChecked() || this.#submitting()) return;
    this.#submitting.set(true);
    this.#error.set(null);

    try {
      for (const { version } of this.pendingDocuments()) {
        const { error } = await this.legalConsentService.consent(version.id, 'gate');
        if (error) throw error;
      }
      await this.legalConsentService.loadMyConsents();
      // `pendingDocuments()` redevient vide dès que `myConsents` inclut ces versions : la gate se
      // referme d'elle-même via les computed ci-dessus, pas de fermeture manuelle à déclencher ici.
      this.#checkedVersionIds.set(new Set());
      this.#modalOpenedManually.set(false);
    } catch (error) {
      console.error('[LegalConsentGateComponent] acceptAll failed', error);
      this.#error.set('Une erreur est survenue lors de l\'enregistrement de votre consentement. Merci de réessayer.');
    } finally {
      this.#submitting.set(false);
    }
  }
}
